import React from 'react';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';
import { FiCalendar, FiCheckCircle, FiChevronDown, FiTag } from 'react-icons/fi';
import { Badge } from '@/components/ui/badge';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { PlayfulCardButton } from '@/components/custom/playful-card';
import usePermission from '@/hooks/usePermission';
import { useSession } from '@/hooks/useSession';
import { getExamStatusMeta } from './exam-status';

const BADGE_CLASSNAME = 'rounded-full px-2.5 py-1 text-xs font-bold';
const MAX_VISIBLE_TAXONOMIES = 2;

// Mesmo padrão de linha do professor usado em Atividades Complementares
// (ComplementaryActivityRowTeacher) e Redação (EssayRow) — lista vertical com
// o mesmo shell (borda + shadow-card), PlayfulCardButton como trigger do menu
// de opções, e bloco de ações à direita (CTA + menu ⋯). As ações além de
// Excluir e Publicar/Despublicar navegam pras telas Default de hoje.
export function ExamRowTeacher({ exam, index = 0, onDelete, onTogglePublish }) {
  const navigate = useNavigate();
  const currentUser = useSession().session.user;
  const statusMeta = getExamStatusMeta(exam);
  const questionCount = exam['question-count'];
  const taxonomies = exam.taxonomies || [];
  const visibleTaxonomies = taxonomies.slice(0, MAX_VISIBLE_TAXONOMIES);
  const extraTaxonomies = taxonomies.slice(MAX_VISIBLE_TAXONOMIES);
  const extraTaxonomiesCount = extraTaxonomies.length;
  // Mesmo comportamento do badge "+X" do Default (BadgeTruncate): título com
  // os nomes das taxonomias restantes, exibido no hover.
  const extraTaxonomiesTitle = extraTaxonomies.map(taxonomy => taxonomy.name).join(', ');

  // Mesma regra de ExamCard.jsx: conteúdo autoral (authored-by-user) só pode
  // ser editado/excluído/publicado pelo próprio criador ou por um admin;
  // conteúdo não autoral segue só a permissão de perfil.
  const isCreatorOrAdmin = !exam?.['authored-by-user'] || (exam?.['authored-by-user'] && currentUser?.id === exam?.creator?.id) || currentUser?.profileNames?.includes('Administrador');

  const hasViewResultsPermission = usePermission('Visualizar resultados', 'Provas e simulados');
  const hasManagePermission = usePermission('Gerenciar provas', 'Provas e simulados');
  const hasCreatePermission = usePermission('Criar provas e simulados', 'Provas e simulados');
  const hasEditPermission = usePermission('Editar provas', 'Provas e simulados');
  const hasDeletePermission = usePermission('Excluir provas', 'Provas e simulados');

  const isRealizada = exam.status === 'Realizada';
  const isNaoRealizada = exam.status === 'Não realizada';

  // Mesmo gate por status do rodapé de ações do ExamCard.jsx (Default):
  // "Realizada"/"Em andamento" exigem "Visualizar resultados"; "Não
  // realizada" exige "Criar provas e simulados" — permissões diferentes por
  // status, não é engano.
  const footerAllowed = isNaoRealizada ? hasCreatePermission : hasViewResultsPermission;
  const canManage = footerAllowed && hasManagePermission;
  // "Resultados" nunca aparece pra prova "Não realizada" (Default não tem
  // o que resultar ainda).
  const canViewResults = footerAllowed && hasViewResultsPermission && !isNaoRealizada;

  // Editar/Excluir/Prova offline não existem pra "Realizada" no Default (o
  // card não tem dropdown nesse status). Diferente do Default — que só
  // exige ser criador/admin em "Em andamento", não em "Não realizada" —,
  // aqui a proteção de criador/admin vale pros dois status com dropdown,
  // por decisão explícita: essa assimetria no Default parecia um descuido,
  // não regra de negócio.
  const dropdownAllowedByStatus = footerAllowed && !isRealizada;
  const canEdit = dropdownAllowedByStatus && hasEditPermission && isCreatorOrAdmin;
  const canDelete = dropdownAllowedByStatus && hasDeletePermission && isCreatorOrAdmin;
  // Publicar/despublicar e Imprimir ficam no header do card no Default —
  // sempre disponíveis (só a permissão/criador ou a contagem de questões
  // importa), independente do status da prova.
  const canPublish = hasManagePermission && isCreatorOrAdmin;
  // Mesma regra de ExamCard.jsx: imprimir/offline só fazem sentido com
  // questões cadastradas e até o limite de 100 suportado pela geração do PDF.
  const canPrintOrOffline = questionCount > 0 && questionCount <= 100;
  const canOffline = dropdownAllowedByStatus && canPrintOrOffline && hasEditPermission && isCreatorOrAdmin;
  const showActionsMenu = canPublish || canPrintOrOffline || canEdit || canDelete || canOffline;

  const meta = [
    { icon: FiCheckCircle, text: `${questionCount} questões` },
    visibleTaxonomies.length > 0 ? { icon: FiTag, text: visibleTaxonomies.map(taxonomy => taxonomy.name).join(' | '), extraCount: extraTaxonomiesCount, extraTitle: extraTaxonomiesTitle } : null,
    exam['published-at'] ? { icon: FiCalendar, text: `Publicada em ${moment(exam['published-at']).format('DD/MM/YYYY')}` } : null
  ].filter(Boolean);

  const handleTogglePublish = () => onTogglePublish(exam);

  return (
    <div
      className="flex flex-wrap items-center gap-4 rounded-[16px] border border-neutral-100 bg-white p-4 shadow-card transition-shadow duration-150 hover:shadow-card-hover"
      style={{ animation: `trilha-in 380ms cubic-bezier(0.23, 1, 0.32, 1) ${250 + index * 70}ms both` }}
      data-testid="exam-row-teacher"
      data-title={exam.title}
    >
      <div className="min-w-[200px] flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <h3
            className="mb-0 line-clamp-2 text-base font-extrabold leading-snug"
            style={{ color: '#222731' }}
            title={exam.title}
          >
            {exam.title}
          </h3>

          <Badge
            variant={exam.published ? 'success-soft' : 'muted'}
            className={`shrink-0 ${BADGE_CLASSNAME}`}
          >
            {exam.published ? 'Publicado' : 'Não publicado'}
          </Badge>

          <Badge
            variant={statusMeta.badgeVariant}
            className={`shrink-0 ${BADGE_CLASSNAME}`}
          >
            {exam.status}
          </Badge>
        </div>

        <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs font-semibold text-neutral-500">
          {meta.map((item, metaIndex) => (
            <span
              key={metaIndex}
              className="flex items-center gap-1.5"
            >
              {item.icon && <item.icon className="h-3.5 w-3.5 text-mission" />}
              {item.text}
              {item.extraCount > 0 && (
                <Badge
                  variant="muted"
                  size="xs"
                  className="rounded-full px-2 font-bold"
                  title={item.extraTitle}
                >
                  +{item.extraCount}
                </Badge>
              )}
            </span>
          ))}
        </div>
      </div>

      <div className="flex shrink-0 items-center gap-2">
        {canManage && (
          <PlayfulCardButton
            className="h-9 w-auto px-6"
            onClick={() => navigate(`/provas/gerenciar/${exam.id}`)}
          >
            Gerenciar
          </PlayfulCardButton>
        )}

        {canViewResults && (
          <PlayfulCardButton
            variant="outline"
            className="h-9 w-auto px-6"
            onClick={() => navigate(`/resultados-prova/${exam.id}`)}
          >
            Resultados
          </PlayfulCardButton>
        )}

        {showActionsMenu && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <PlayfulCardButton
                variant="outline"
                aria-label="Opções"
                className="h-9 w-9 shrink-0 gap-0 p-0"
              >
                <FiChevronDown size={20} />
              </PlayfulCardButton>
            </DropdownMenuTrigger>

            <DropdownMenuContent align="end">
              {canPublish && <DropdownMenuItem onSelect={handleTogglePublish}>{exam.published ? 'Despublicar' : 'Publicar'}</DropdownMenuItem>}
              {canEdit && <DropdownMenuItem onSelect={() => navigate(`/provas/criar/curso/${exam.id}`)}>Editar</DropdownMenuItem>}
              {canPrintOrOffline && <DropdownMenuItem onSelect={() => navigate(`/avaliacoes/provas/impressao/${exam.id}`)}>Imprimir</DropdownMenuItem>}
              {canOffline && <DropdownMenuItem onSelect={() => navigate(`/avaliacoes/provas/offline/${exam.id}`)}>Prova offline</DropdownMenuItem>}
              {canDelete && <DropdownMenuItem onSelect={() => onDelete(exam)}>Excluir</DropdownMenuItem>}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
    </div>
  );
}
